import { styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RETIREMENT_CATEGORY_GOALS, WEALTH_CATEGORY_GOALS } from '../../common/constants';
import {
    getFormattedDate,
    getHeaderIconFromType,
    groupGoalsByLastModified,
    groupGoalsByPriority
} from '../../common/helper';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { GoalTypeProps } from '../../common/types/clients-entity';
import { ComboSelect, Typography } from '../../components/atoms';
import { NoGoal } from '../../components/molecules';
import { GoalSummaryChip } from '../../components/molecules/GoalSummaryChip/GoalSummaryChip';
import { GoalType } from '../../components/molecules/GoalType/GoalType';
import { WealthGoals } from '../../containers/wealth-goals-drawer';
import { RetirementGoalsFF } from '../../containers/retirement-goals-drawer/RetirementGoalsDrawerFF';
import { AdviceGoalPayloadType, syncAdviceGoals } from '../../features/client-goals';
import { calculateYearsBetweenDates } from '../../utils/index';
import dayjs from 'dayjs';
import { useNavigate, useLocation } from 'react-router-dom';
import { ADVISOR_ROUTES } from '../../routes/routeConstants';

const GoalsContainer = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column'
}));
const PageContainer = styled('div')(() => ({
    width: '100%',
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: '16px'
}));
const DisplayRow = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'row'
}));
const GoalsChipsSection = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    columnGap: theme.spacing(2),
    rowGap: '25px',
    marginTop: '6px'
}));
const FilterContainer = styled('div')(() => ({
    width: '240px',
    marginLeft: 'auto',
    marginTop: '20px',
    marginBottom: '10px'
}));

const GoalTypeContainer = styled('div')(() => ({
    display: 'flex',
    gap: '24px',
    alignItems: 'center'
}));

const StyledHeader = styled('div')(() => ({
    display: 'flex',
    alignItems: 'center'
}));

export const CreateGoalsStep = ({
    goalsWithNoFundingSource,
    goalsWithMissingDates
}: {
    goalsWithNoFundingSource: AdviceGoalPayloadType['formFields'];
    goalsWithMissingDates: AdviceGoalPayloadType['formFields'];
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const adviceGoals = useAppSelector((state) => state.adviceGoals);
    const { formFields } = adviceGoals;
    const [displayedFormFields, setDisplayedFormFields] = useState(formFields || []);
    const dispatch = useAppDispatch();
    const [showSelectedGoalTypeDrawer, setShowSelectedGoalTypeDrawer] = useState<GoalTypeProps | undefined>();
    const [showRetirementGoalDrawer, setShowRetirementGoalDrawer] = useState<GoalTypeProps | undefined>();
    const focusedType = useMemo(() => showSelectedGoalTypeDrawer?.type, [showSelectedGoalTypeDrawer]);
    const [currentGoalData, setCurrentGoalData] = useState<any>();
    const [yearsInRetirement, setYearsInRetirement] = useState('0');
    const [sortByValue, setSortByValue] = useState('Last Modified');
    const sortFormFieldsByPriority = () => {
        const sorted = groupGoalsByPriority([...formFields], ({ data }: { data: any }) => data.goalPriority);
        setDisplayedFormFields(sorted);
    };
    const sortFormFieldsByLastUpdated = () => {
        const sorted = groupGoalsByLastModified([...formFields]);
        setDisplayedFormFields(sorted);
    };
    const handleFilterChange = (value: string) => {
        setSortByValue(value);
    };

    useEffect(() => {
        if (location.pathname !== ADVISOR_ROUTES.GOALS) navigate(ADVISOR_ROUTES.GOALS, { replace: true });
    }, []);

    useEffect(() => {
        switch (sortByValue) {
            case 'Priority':
                sortFormFieldsByPriority();
                break;
            case 'Last Modified':
                sortFormFieldsByLastUpdated();
                break;
            default:
                break;
        }
    }, [formFields, sortByValue]);

    const goalDeleted = (goalId: string) => {
        const newFormFields = displayedFormFields.filter((goal) => goal.goalId !== goalId);
        dispatch(
            syncAdviceGoals({
                ...adviceGoals,
                formFields: newFormFields,
                response: []
            })
        );
    };

    return (
        <>
            <PageContainer>
                <DisplayRow sx={{ gap: '40px', marginBottom: '16px', padding: '12px 0' }}>
                    <GoalsContainer>
                        <Typography
                            label={t('ADD_GOALS')}
                            variant="headers.h4"
                            marginBottom="16px"
                            color="neutral.black"
                        />
                        <GoalTypeContainer>
                            {RETIREMENT_CATEGORY_GOALS.map((g: GoalTypeProps, key) => {
                                // Change retirement goals props at src/common/constants.ts
                                return (
                                    <GoalType
                                        type={g.type}
                                        key={key}
                                        size="large"
                                        onClickHandler={() => setShowRetirementGoalDrawer(g)}
                                        isFocused={focusedType}
                                        adviceGoals={adviceGoals}
                                    />
                                );
                            })}
                            {WEALTH_CATEGORY_GOALS.map((g: GoalTypeProps, key) => {
                                // Change wealth goals props at src/common/constants.ts
                                return (
                                    <GoalType
                                        type={g.type}
                                        key={key}
                                        size="large"
                                        onClickHandler={() => setShowSelectedGoalTypeDrawer(g)}
                                        isFocused={focusedType}
                                        adviceGoals={adviceGoals}
                                    />
                                );
                            })}
                        </GoalTypeContainer>
                    </GoalsContainer>
                </DisplayRow>
                {displayedFormFields?.length > 0 && (
                    <StyledHeader>
                        <div>
                            <Typography variant="headers.h4">{t('SAVED_GOALS')}</Typography>
                        </div>
                        <FilterContainer>
                            <ComboSelect
                                preText={t('SORT_BY')}
                                options={[
                                    { label: t('TEXT_ADMIN_CLIENTS_TABLE_HEADER_6'), value: 'Last Modified' },
                                    { label: t('PRIORITY_LABEL'), value: 'Priority' }
                                ]}
                                selectedValue={sortByValue}
                                onSelectChange={handleFilterChange}
                                sx={{
                                    '& .MuiSelect-select': {
                                        fontWeight: 400
                                    }
                                }}
                            />
                        </FilterContainer>
                    </StyledHeader>
                )}
                {displayedFormFields?.length > 0 ? (
                    <GoalsChipsSection>
                        {displayedFormFields.map((goals: any) => {
                            const isRetirement = goals?.data?.wealthGoalType?.toLowerCase?.() === 'retirement';

                            const targetDate = isRetirement
                                ? goals.data.planStartRetirement
                                : goals?.data?.goalDate || goals?.data?.targetDate;
                            const planningDate = isRetirement
                                ? getFormattedDate(dayjs(goals?.data?.targetDate, 'DD-MM-YYYY')) // ref: AEX-2198 CommentId=1879302 point #2
                                : null;
                            let isNoFundingSource = goalsWithNoFundingSource?.find((g) => g.goalId === goals.goalId);
                            let isMissingDates = goalsWithMissingDates?.find(
                                (g: { goalId: any }) => g.goalId === goals.goalId
                            );
                            const showErrorBorder = isNoFundingSource || isMissingDates;
                            return (
                                <GoalSummaryChip
                                    key={goals.goalId}
                                    headerIcon={
                                        <GoalType
                                            type={getHeaderIconFromType(goals?.data?.wealthGoalType) || 'buyahouse'}
                                        />
                                    }
                                    goalType={goals?.data?.wealthGoalType}
                                    goalId={goals?.goalId}
                                    onGoalDelete={goalDeleted}
                                    planningDate={planningDate}
                                    onEdit={() => {
                                        let data = {};
                                        if (goals.data.goalType === 'WEALTH_GOAL') {
                                            data = {
                                                goalAmount:
                                                    goals?.data?.goalAmount || goals?.data?.targetedRetirementIncome,
                                                goalId: goals?.goalId ?? goals.data?.goalId,
                                                goalPriority: goals.data?.goalPriority,
                                                goalName: goals.data?.goalName,
                                                targetDate: goals?.data?.goalDate || goals?.data?.targetDate,
                                                title: goals?.data?.wealthGoalType
                                            };
                                        } else {
                                            const yearsInRetirement = calculateYearsBetweenDates(
                                                goals?.data?.planStartRetirement,
                                                goals?.data?.targetDate
                                            );
                                            setYearsInRetirement(yearsInRetirement);
                                            data = {
                                                goalAmount: goals?.data?.goalAmount,
                                                goalPriority: goals?.data?.goalPriority,
                                                targetedRetirementIncome: goals?.data?.targetedRetirementIncome,
                                                goalName: goals?.data?.goalName,
                                                yearsInRetirement: yearsInRetirement,
                                                planStartRetirement: goals?.data?.planStartRetirement,
                                                targetDate: goals?.data?.targetDate,
                                                frequency: 'Annually',
                                                title: goals?.data?.wealthGoalType,
                                                ...goals.data,
                                                goalId: goals?.goalId ?? goals.data?.goalId
                                            };
                                        }
                                        setCurrentGoalData(data);
                                        goals.data.goalType === 'WEALTH_GOAL'
                                            ? setShowSelectedGoalTypeDrawer(goals.data)
                                            : setShowRetirementGoalDrawer(goals.data);
                                    }}
                                    goal_name={goals?.data?.goalName}
                                    priority={goals?.data?.goalPriority}
                                    goalAmount={goals?.data?.goalAmount}
                                    targetedRetirementIncome={goals?.data?.targetedRetirementIncome}
                                    target_date_data={getFormattedDate(dayjs(targetDate, 'DD-MM-YYYY'))}
                                    isRetirement={isRetirement}
                                    showErrorBorder={showErrorBorder}
                                />
                            );
                        })}
                    </GoalsChipsSection>
                ) : (
                    <NoGoal title={t('NO_GOALS')} infoText={t('NO_GOALS_INFOTEXT')} />
                )}
            </PageContainer>
            {currentGoalData ? (
                <>
                    <RetirementGoalsFF
                        selectedGoal={showRetirementGoalDrawer}
                        handleClose={() => {
                            setCurrentGoalData(undefined);
                            setShowRetirementGoalDrawer(undefined);
                        }}
                        yearsInRetirement={parseInt(yearsInRetirement)}
                        open={Boolean(showRetirementGoalDrawer)}
                        goalData={currentGoalData}
                    />
                    <WealthGoals
                        selectedGoal={showSelectedGoalTypeDrawer}
                        handleClose={() => {
                            setCurrentGoalData(undefined);
                            setShowSelectedGoalTypeDrawer(undefined);
                        }}
                        open={Boolean(showSelectedGoalTypeDrawer)}
                        goalData={currentGoalData}
                    />
                </>
            ) : (
                <>
                    <WealthGoals
                        selectedGoal={showSelectedGoalTypeDrawer}
                        handleClose={() => setShowSelectedGoalTypeDrawer(undefined)}
                        open={Boolean(showSelectedGoalTypeDrawer)}
                    />
                    <RetirementGoalsFF
                        selectedGoal={showRetirementGoalDrawer}
                        handleClose={() => setShowRetirementGoalDrawer(undefined)}
                        open={Boolean(showRetirementGoalDrawer)}
                    />
                </>
            )}
        </>
    );
};
//This line is for test purpose only
